'use client'

import { usePathname } from "next/navigation"
import { ReactNode, useEffect, useState } from "react"
import { XMarkIcon } from "@heroicons/react/20/solid"
import classNames from "classnames"
import { Menu } from "@headlessui/react"
import { UserNav } from "../UserNav"
import { Session } from "next-auth"
import { getPreviewModeData } from "@/app/actions"
import Link from "next/link"
import { createContext } from "react"
import { Body } from "../Body"

export interface LinkType {
  type: 'a' | 'Link'
  href: string
  title: string
  className: string
  text: string
  prefetch?: boolean
}

export interface PreviewBarProps {
  links: LinkType[]
  hasDiff: boolean
  state: 'published' | 'draft' | 'archived' | 'code' | 'loading'
  log: string
  diffMode: number
  session: Session | null
}

export const PreviewBarContext = createContext<stateOptions["modes"]|null>(null)

export function PreviewBarClient({ isEnabled, children }: {
  isEnabled: boolean
  children: ReactNode
}) {

  const pathname = usePathname()
  const [params, setParams] = useState<PreviewBarProps|null>(null)

  useEffect(() => {
    const getParams = () => {
      getPreviewModeData(pathname).then((params) => {
        setParams(params)
      })
    }

    if (isEnabled) {
      getParams()
    }
  }, [isEnabled, pathname])

  if (!isEnabled) {
    return (
      <PreviewBarContext.Provider value={null}>
        {children}
      </PreviewBarContext.Provider>
    )
  }

  if (params === null) {
    return (
      <PreviewBarRender links={[]} hasDiff={false} state="loading" log={''} diffMode={0} session={null}>
        {children}
      </PreviewBarRender>
    )
  }

  return (
    <PreviewBarRender {...params}>
      {children}
    </PreviewBarRender>
  )
}

export interface stateOptions {
  modes: 'revision' | 'diff' | 'current'
}

export function PreviewBarRender({links, hasDiff, state, log, diffMode, session, children}: PreviewBarProps & {
  children: ReactNode
}) {
  const [inIframe, setInIframe] = useState(true)
  const [mode, setMode] = useState(diffMode)
  const [toggleState, setToggleState] = useState(false)

  useEffect(() =>  {
    setInIframe(typeof window !== "undefined" && window.self != window.top)
  }, [])
  const pathname = usePathname()
  const toggleModes:stateOptions["modes"][] = ['revision', 'diff', 'current']

  function getViewMode(): stateOptions["modes"] {
    return toggleModes[mode % toggleModes.length]
  }

  const higlightMap:{ 
    [P in PreviewBarProps["state"]]?: string 
  } = {
    published: 'bg-teal-400',
    draft: 'bg-yellow-400',
  }

  const highlight = (state.length && Object.keys(higlightMap).indexOf(state) !== -1)?
  higlightMap[state] : 'bg-gray-400'

  const diffHighlightMap:Record<stateOptions["modes"], string> = {
    revision: 'orange',
    diff: 'pink',
    current: 'teal'
  };

   // Registering classnames with Tailwind.
  [
    'peer-checked:bg-teal-600', 'bg-teal-600', 'ring-teal-300',
    'peer-checked:bg-pink-600', 'bg-pink-600', 'ring-pink-300',
    'peer-checked:bg-orange-600', 'bg-orange-600', 'ring-orange-300',
  ]

  const diffHlTag = 'bg-' + diffHighlightMap[getViewMode()] + '-600';
  const diffHighlight = [ toggleState ? 'peer-checked:' + diffHlTag : diffHlTag]
  diffHighlight.push('ring-' + diffHighlightMap[getViewMode()] + '-300')


  const state_message_map:Record<PreviewBarProps["state"], string> = {
    published: 'Published',
    draft: 'In draft',
    archived: 'Archived',
    code: 'In code',
    loading: 'Loading...'
  }

  function renderLink(link: LinkType) {
    if (link.type == "a") {
      return <a href={link.href} title={link.title} className={link.className}>{link.text}</a>
    }
    else {
      return <Link href={link.href} title={link.title} prefetch={link.prefetch} className={link.className}>{link.text}</Link>
    }
  }

  return (
    <>
      <div className={`sticky top-0 z-10 ${highlight} drop-shadow`}>
        {!inIframe && <a 
          title="Close preview mode" 
          href={'/api/end-preview?slug=' + pathname} 
          className="transition absolute right-0 m-2 hover:bg-white/50 rounded py-2 px-4 flex text-sm"><XMarkIcon className='w-4 overflow-visible' /></a>}  
        
        <div className="container mx-auto p-3 flex flex-row gap-3 items-center">
            <div>
              <div className="font-display">Preview mode</div>
              <blockquote className="italic flex gap-3">
                <div>{state_message_map[state]}</div>
                <Body value={log} />
              </blockquote>
            </div>
            { hasDiff && (
              <div className="pl-4">
                <label className="relative inline-flex items-center cursor-pointer">
                    <input 
                      type="checkbox" 
                      className="sr-only peer" 
                      checked={toggleState} 
                      onChange={() => {
                        setToggleState(!toggleState) 
                        setMode(mode + 1)
                      }} 
                    />
                    <div className={classNames(
                      'w-11 h-6 bg-gray-200 outline-none ring-4 rounded-full', 
                      'peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white',
                      'after:content-[\'\'] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all',
                      diffHighlight
                    )}></div>
                    <span className="ms-3 text-sm font-medium text-gray-900 dark:text-gray-300">Showing {getViewMode()}</span>
                </label>
              </div>
            )}
            
            {/* Only display when preview is embedded inside an iframe. */}
            {!inIframe && (
              <>
                { (links.length > 0) && <div className="flex justify-end grow transition-colors duration-300 relative">
                  <Menu>
                    <Menu.Button className="bg-black/20 text-white after:bg-white font-bold rounded px-5 py-2 hover:bg-black/30 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/75 chevron-down-icon">Options</Menu.Button>
                    <Menu.Items className="absolute flex flex-col right-0 mt-12 w-56 origin-top-right divide-y divide-gray-600 rounded-md bg-white shadow-lg ring-1 ring-black/5 focus:outline-none transform opacity-100 scale-100 p-1">
                      {links.map((link, index) => <Menu.Item key={`menu-item-${index}`}>{renderLink(link)}</Menu.Item>)}
                    </Menu.Items>
                  </Menu>
                </div> }
                <div className={links.length ? "" : "flex grow justify-end"}>
                  <UserNav 
                    signOutLink={false}
                    session={session}
                    removeClassName={['fixed', 'top-0', 'right-0', 'mt-4', 'mr-3']}
                    className={['relative', 'place-self-end']} />
                </div>
              </>
            )}
        </div>
      </div>
      <PreviewBarContext.Provider value={getViewMode()}>
        {children}
      </PreviewBarContext.Provider>
    </>
  )
}